import { Composer } from "@somethings-ui/web";
import { ArrowUp, Loader2 } from "lucide-react";
import { useCallback, type ChangeEvent, type KeyboardEvent } from "react";

interface AiChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  teenName: string;
  loading?: boolean;
  disabled?: boolean;
}

export function AiChatComposer({
  value,
  onChange,
  onSubmit,
  teenName,
  loading = false,
  disabled = false,
}: AiChatComposerProps) {
  const name = teenName.trim().length > 0 ? teenName.trim() : "your mentee";
  const placeholder = `Ask about ${name}`;
  const canSubmit = !disabled && !loading && value.trim().length > 0;

  const resize = useCallback((element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value);
      resize(event.currentTarget);
    },
    [onChange, resize],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        if (canSubmit) onSubmit();
      }
    },
    [canSubmit, onSubmit],
  );

  return (
    <div className="shrink-0 border-t border-surfaces-divider px-4 py-3">
      <div className="flex items-center gap-2 rounded-full border border-surfaces-divider bg-white px-4 py-2">
        <Composer.Input
          rows={1}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={placeholder}
          className="max-h-32 min-h-6 flex-1 self-center py-1"
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          aria-label="Send question"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-text-primary text-white transition-opacity hover:opacity-90 focus-visible:outline-none disabled:opacity-30"
        >
          {loading ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <ArrowUp className="size-4" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}
